import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import chartColorPalette from 'assets/styles/chartColorPalette';
import MasterFilter, {
  type DateRangeValue,
  type FilterFieldConfig,
  type FilterOption,
  useMasterFilter,
} from 'components/common/MasterFilter';
import PageTitle from 'components/UI/PageTitle';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import useIsAdmin from 'hooks/useIsAdmin';
import moment from 'moment';
import { useMemo } from 'react';
import {
  useGetAdminBusinessUnitCostChartsQuery,
  useGetAdminBusinessUnitQuery,
  useGetBusinessUnitSourcesQuery,
} from 'services/api/businessUnitCharts.api';
import priceFormat from 'services/format/priceFormat';
import { NAVY, RADIUS_CARD, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import ChartCard from '../Dashboard/components/ChartCard';

const EMPTY_SOURCES: string[] = [];

// Cost-split series colors follow the design-system chart palette order (§16): AI, WhatsApp, Floid.
const SPLIT_COLORS = chartColorPalette.slice(0, 3);

// Shared MUI x-charts styling to match the Dashboard look (faint navy axes, soft grid).
const CHART_AXIS_SX = {
  '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: alpha(NAVY, 0.1) },
  '& .MuiChartsGrid-line': { stroke: alpha(NAVY, 0.06) },
  '& .MuiChartsAxis-left .MuiChartsAxis-label': { transform: 'translate(-8px, 0)' },
} as const;
const TICK_LABEL_STYLE = { fontSize: 11, fill: alpha(NAVY, 0.55) } as const;

const BUSINESS_UNIT_CHARTS_FILTER: FilterFieldConfig[] = [
  {
    key: 'dateRange',
    kind: 'dateRange',
    label: 'Fecha creación',
    placement: 'bar',
    defaultValue: { start: moment().subtract(7, 'days').startOf('day'), end: moment().endOf('day') },
  },
  { key: 'sources', kind: 'multiSelect', label: 'Origen', placement: 'bar' },
];

type CostSplit = { ai: number; whatsApp: number; floid: number };

/** Stacked area of the AI / WhatsApp / Floid cost split over time (design system §16 palette). */
const StackedCostChart = ({
  split,
  yLabel,
  scaleType = 'band',
  tickMinStep,
}: {
  split: Record<string, CostSplit>;
  yLabel: string;
  scaleType?: 'band' | 'point';
  tickMinStep?: number;
}) => {
  const days = Object.keys(split);
  return (
    <LineChart
      colors={SPLIT_COLORS}
      series={[
        { data: days.map((d) => split[d].ai), label: 'AI', area: true, stack: 'cost', showMark: false },
        { data: days.map((d) => split[d].whatsApp), label: 'WhatsApp', area: true, stack: 'cost', showMark: false },
        { data: days.map((d) => split[d].floid), label: 'Floid', area: true, stack: 'cost', showMark: false },
      ]}
      height={290}
      xAxis={[
        {
          data: days,
          scaleType,
          label: 'Fecha',
          tickLabelStyle: { ...TICK_LABEL_STYLE, angle: 45, textAnchor: 'start' },
        },
      ]}
      yAxis={[{ label: yLabel, min: 0, tickMinStep }]}
      margin={{ top: 16, bottom: 80, left: 40, right: 80 }}
      grid={{ horizontal: true }}
      sx={CHART_AXIS_SX}
      slotProps={{ legend: { position: { vertical: 'top', horizontal: 'end' } } }}
    />
  );
};

const BusinessUnitCharts = () => {
  // Driven by the global BU selector in the sidebar; reacts to changes automatically via the hook.
  const { selectedBusinessUnitId } = useSelectedBusinessUnit();
  const buId = selectedBusinessUnitId ?? 0;
  const isAdmin = useIsAdmin();

  const filter = useMasterFilter(BUSINESS_UNIT_CHARTS_FILTER, buId, undefined, undefined, {
    urlNamespace: 'buCharts',
  });

  const dateRange = filter.values.dateRange as DateRangeValue | undefined;
  const rawSources = filter.values.sources as string[] | undefined;
  // Stable identity: a fresh `[]` each render would retrigger the query/effect forever.
  const sources = useMemo(() => rawSources ?? EMPTY_SOURCES, [rawSources]);
  const startDate = (dateRange?.start ?? moment().subtract(7, 'days').startOf('day')).toISOString();
  const endDate = (dateRange?.end ?? moment().endOf('day')).toISOString();

  const { data: businessUnit, isLoading: buLoading } = useGetAdminBusinessUnitQuery(buId, { skip: !buId });
  const { data: businessUnitCostsCharts, isFetching: chartsFetching } = useGetAdminBusinessUnitCostChartsQuery(
    { id: buId, startDate, endDate, sources },
    { skip: !buId }
  );
  // Source options from the BU's configured client sources (same list as the Clients page bar).
  const { data: buSources } = useGetBusinessUnitSourcesQuery({ businessUnitId: buId }, { skip: !buId });
  const dynamicOptions = useMemo(
    () => ({
      sources: (buSources ?? []).filter(Boolean).map((s): FilterOption => ({ value: s, label: s })),
    }),
    [buSources]
  );

  if (!isAdmin) {
    return <Typography variant="h3">No tienes acceso a esta página</Typography>;
  }
  if (!buId) {
    return <Typography variant="h3">Selecciona una unidad de negocio en el selector lateral</Typography>;
  }
  if (buLoading) {
    return <LoadingSpinner />;
  }
  if (!businessUnit) {
    return <Typography variant="h3">No se encontró la empresa</Typography>;
  }

  const totalAICost = businessUnitCostsCharts?.totalAICost || 0;
  const totalWhatsAppCost = businessUnitCostsCharts?.totalWhatsAppCost || 0;
  const totalFloidCost = businessUnitCostsCharts?.totalFloidRequestsCost || 0;
  // Total reconciles with the breakdown by summing the live components; the stored client.cost
  // snapshot drifts from the recomputed AI/WhatsApp/Floid values.
  const totalCost = totalAICost + totalWhatsAppCost + totalFloidCost;
  const totalClients = businessUnitCostsCharts?.totalClients || 0;
  const totalLeads = businessUnitCostsCharts?.totalLeads || 0;
  const avgPerClient = totalClients > 0 ? totalCost / totalClients : 0;
  const avgPerLead = totalLeads > 0 ? totalCost / totalLeads : 0;
  // Share of the total cost each component represents (AI + WhatsApp + Floid = totalCost).
  const pctOfTotal = (amount: number) => (totalCost > 0 ? `${Math.round((amount / totalCost) * 100)}% del total` : '');

  return (
    <Box sx={{ py: 3, px: { xs: 2, lg: 4 } }}>
      <Box sx={{ mb: 3 }}>
        <PageTitle>{businessUnit.name}</PageTitle>
      </Box>

      <Box sx={{ mb: 3 }}>
        <MasterFilter {...filter} dynamicOptions={dynamicOptions} />
      </Box>

      {chartsFetching ? (
        <LoadingSpinner />
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <KpiTile label="Costo total" value={priceFormat.format(totalCost)} />
            <KpiTile label="Costo AI" value={priceFormat.format(totalAICost)} hint={pctOfTotal(totalAICost)} />
            <KpiTile
              label="Costo WhatsApp"
              value={priceFormat.format(totalWhatsAppCost)}
              hint={pctOfTotal(totalWhatsAppCost)}
            />
            <KpiTile label="Costo Floid" value={priceFormat.format(totalFloidCost)} hint={pctOfTotal(totalFloidCost)} />
            <KpiTile
              label="Costo promedio por cliente"
              value={priceFormat.format(avgPerClient)}
              hint={`${totalClients} clientes`}
            />
            <KpiTile
              label="Costo promedio por lead"
              value={priceFormat.format(avgPerLead)}
              hint={`${totalLeads} leads`}
            />
          </Box>

          <ChartCard
            title="Costos por cliente en el tiempo"
            description="Costo promedio por cliente creado un día en específico, desglosado en AI, WhatsApp (templates) y Floid"
          >
            <StackedCostChart
              split={businessUnitCostsCharts?.averageCostSplitPerDay || {}}
              yLabel="Costo ($)"
              scaleType="point"
            />
          </ChartCard>

          <ChartCard
            title="Costos por lead en el tiempo"
            description="Costo promedio por lead creado un día en específico, desglosado en AI, WhatsApp (templates) y Floid"
          >
            <StackedCostChart
              split={businessUnitCostsCharts?.costPerLeadSplitPerDay || {}}
              yLabel="Costo por lead ($)"
            />
          </ChartCard>

          <ChartCard
            title="Costos promedio de un mensaje por día"
            description="Costo promedio de un mensaje por día, desglosado en AI, WhatsApp (templates) y Floid. No se consideran mensajes no enviados"
          >
            <StackedCostChart
              split={businessUnitCostsCharts?.averageMessageCostSplitPerDay || {}}
              yLabel="Costo por mensaje ($)"
              tickMinStep={10}
            />
          </ChartCard>

          <ChartCard
            title="Costos por cliente"
            description="Cantidad de clientes que nos cuestan cada uno de estos rangos (WhatsApp, AI y floid)"
          >
            <BarChart
              colors={[alpha(NAVY, 0.65)]}
              series={[{ data: (businessUnitCostsCharts?.costRangesCount || []).map((costRange) => costRange.count) }]}
              height={290}
              xAxis={[
                {
                  data: (businessUnitCostsCharts?.costRangesCount || []).map(
                    (costRange) => `<${priceFormat.format(costRange.range[1])}`
                  ),
                  scaleType: 'band',
                  label: 'Costo del cliente ($)',
                  tickLabelStyle: TICK_LABEL_STYLE,
                },
              ]}
              yAxis={[{ label: 'Cantidad de clientes', min: 0, tickMinStep: 1 }]}
              margin={{ top: 16, bottom: 110, left: 40, right: 40 }}
              grid={{ horizontal: true }}
              sx={CHART_AXIS_SX}
            />
          </ChartCard>
        </>
      )}
    </Box>
  );
};

const LoadingSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
    <CircularProgress sx={{ color: NAVY }} />
  </Box>
);

/** Small surface-lift stat tile, navy text, no borders (design system §2/§4). */
const KpiTile = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <Box
    sx={{
      flex: '1 1 180px',
      backgroundColor: SURFACE_CONTAINER_LOWEST,
      borderRadius: RADIUS_CARD,
      boxShadow: SHADOW,
      px: 3,
      py: 2,
      minWidth: 180,
    }}
  >
    <Typography
      sx={{
        fontFamily: 'Jost, sans-serif',
        fontWeight: 600,
        fontSize: '0.7rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: alpha(NAVY, 0.45),
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: '1.6rem', color: NAVY }}>
      {value}
    </Typography>
    {hint && (
      <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: alpha(NAVY, 0.4), mt: 0.25 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

export default BusinessUnitCharts;
